import React, {
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import mergeRefs from 'merge-refs';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import Icon, {Icons} from '@pb/kit-component-icon';
import {PillBase} from './pill-base.component';

import {useArrowNavigationItem} from '@pb/kit-hook-use-arrow-navigation';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {StripOfPillsContext} from '../strip-of-pills.context';

import styles from './pill.module.styl';

import type {Item} from '../typedef';
import type {ForwardedRef, ReactNode, Ref} from 'react';
import type {PillBaseProps} from './pill-base.component';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

const animationNameRemove = 'remove';
const noop = () => undefined;

type refType = HTMLDivElement | HTMLButtonElement;

export interface PillProps extends Omit<PillBaseProps, 'prefix' | 'name' | 'type'> {
    /**
     * When `true`, renders the pill name in a plain `<span>` with CSS ellipsis
     * instead of the `<EllipsisTextHint>` tooltip. Use when native browser
     * tooltip behavior is undesirable (e.g. inside another tooltip).
     */
    disableBrowserHint?: boolean,
    /**
     * @deprecated Use `className` instead. Will be removed 2026-03-15.
     */
    mix?: string,
    /**
     * Primary text label displayed inside the pill. May be any React node —
     * typically a string but can include formatted text.
     */
    name: ReactNode,
    /**
     * Called after the removal animation completes and the pill is unmounted.
     * Use to update the owning list's state (e.g. remove the item from an array).
     */
    onRemove?: () => void,
    /**
     * Node rendered before `name` inside the pill (e.g. a color swatch, icon,
     * or flag image). Wrapped in a `<span>` with prefix spacing styles.
     */
    prefix?: ReactNode,
    /**
     * Called instead of the default removal handler when the user presses the
     * remove icon or `Backspace` / `Delete`. Use to show a confirmation dialog
     * before committing the removal.
     */
    preventedRemoveHandler?: () => void,
    /**
     * Ref forwarded to the root interactive element (`<button>` when clickable,
     * `<div>` otherwise).
     */
    ref?: Ref<HTMLButtonElement>,
    /**
     * When `true`, a close icon (`×`) is rendered inside the pill. Clicking the
     * icon or pressing `Backspace` / `Delete` triggers the removal animation
     * and then calls `onRemove`.
     */
    removable?: boolean,
    /**
     * When `true`, the removal is triggered on `mousedown` instead of `click`
     * on the close icon. Useful in typeahead inputs where the input re-focuses
     * before a `click` event fires.
     */
    removableOnMouseDown?: boolean,
    /**
     * @deprecated Use `variant` instead. Will be removed 2026-03-15.
     */
    type?: 'DEFAULT' | 'ADDITION' | 'EXCLUSION',
    /**
     * Visual style of the pill. `"ADDITION"` applies a green tint (item being
     * added to a filter); `"EXCLUSION"` applies a red tint (item being excluded);
     * `"DEFAULT"` uses the neutral gray style. Defaults to `"DEFAULT"`.
     */
    variant?: 'DEFAULT' | 'ADDITION' | 'EXCLUSION',
}

/**
 * Small interactive chip that displays a label inside a `<StripOfPills>` or
 * standalone. Renders as a `<button>` when `onClick` or `removable` is set,
 * otherwise as a `<div>`. Supports a remove animation, optional prefix content,
 * three visual variants, and full keyboard interaction (`Backspace` / `Delete`).
 *
 * ## Accessibility
 * - `aria-disabled` reflects the disabled state.
 * - Arrow keys (`ArrowLeft` / `ArrowRight`) cycle focus between pills when
 *   inside `<StripOfPills>`.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/forms-strip-of-pills-pill-input-pill--documentation}
 */

export const Pill = forwardRef<refType, PillProps>(
    function Pill(props: PillProps, ref: ForwardedRef<refType>) {
        const {
            disableBrowserHint,
            mix = '',
            name,
            onClick,
            onMouseDown,
            onMouseUp,
            onMouseLeave,
            onKeyUp,
            onRemove,
            prefix,
            preventedRemoveHandler,
            removable,
            removableOnMouseDown,
            type: typeProp = 'DEFAULT',
            variant: variantProp,
            ...restProps
        } = props;

        const [pressed, setPressed] = useState<boolean>(false);
        const [animated, setAnimated] = useState<boolean>(false);
        const [removed, setRemoved] = useState<boolean>(false);
        const [hidden, setHidden] = useState<boolean>(false);

        const removePillRef = useRef<HTMLSpanElement | null>(null);
        const divRef = useRef<HTMLDivElement | null>(null);
        const buttonRef = useRef<HTMLButtonElement | null>(null);
        const self = useRef<Item>({
            setHidden,
        });

        const interactive = onClick || removable;
        const actualRef = interactive ? buttonRef : divRef;

        const {
            ref: arrowNavigationRef,
            tabIndex,
            addSelf,
            removeSelf,
        } = useArrowNavigationItem();

        useEffect(() => {
            if (hidden) {
                removeSelf();
            } else {
                addSelf();
            }
        }, [hidden]); // eslint-disable-line react-hooks/exhaustive-deps

        const {
            addPill,
            removePill,
        } = useContext(StripOfPillsContext);

        useLayoutEffect(() => {
            const currentSelf = self.current;
            addPill(currentSelf);
            return () => removePill(currentSelf);
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        useImperativeHandle(ref, () => actualRef.current as refType);

        const variant = variantProp || typeProp;

        const handleRemove = useCallback(() => {
            if (preventedRemoveHandler) {
                preventedRemoveHandler();
                return;
            }

            removeSelf();
            removePill(self.current);

            if (actualRef.current) {
                setAnimated(true);
            }

        }, [actualRef, preventedRemoveHandler, removePill, removeSelf]);

        const handleAnimationEnd = (event: React.AnimationEvent<HTMLElement>) => {
            if (event.animationName.includes(animationNameRemove)) {
                setRemoved(true);
                if (onRemove) {
                    onRemove();
                }
            }
        };

        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            const target = event.target as HTMLElement;

            if (onClick && (
                !removePillRef.current
                || (
                    target !== removePillRef.current
                    && !removePillRef.current.contains(target)
                )
            )) {
                onClick(event);
            }
        };

        const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
            setPressed(true);
            if (onMouseDown) {
                onMouseDown(event);
            }
        };

        const handleMouseUp = (event: React.MouseEvent<HTMLButtonElement>) => {
            handleMouseLeave();
            if (onMouseUp) {
                onMouseUp(event);
            }
        };

        const hasPillHoverClass = () => {
            return actualRef.current?.classList.contains(getPillHoverClassName(variant));
        };

        const handleMouseEnterRemove = () => {
            if (actualRef.current && !hasPillHoverClass()) {
                actualRef.current.classList.add(getPillHoverClassName(variant));
            }
        };

        const handleMouseLeave = (event?: React.MouseEvent<HTMLButtonElement>) => {
            setPressed(false);
            if (actualRef.current && hasPillHoverClass()) {
                actualRef.current.classList.remove(getPillHoverClassName(variant));
            }
            if (onMouseLeave && event) {
                onMouseLeave(event);
            }
        };

        const handleKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (event.key === 'Backspace' || event.key === 'Delete') {
                handleRemove();
            }

            if (onKeyUp) {
                onKeyUp(event);
            }
        };

        const getPillHoverClassName = (pillVariant: 'DEFAULT' | 'ADDITION' | 'EXCLUSION') => {
            switch (pillVariant) {
                case 'ADDITION':
                    return 'pill_addition__hovered';
                case 'EXCLUSION':
                    return 'pill_exclusion__hovered';
                default:
                    return 'pill__hovered';
            }
        };

        const pillClassName = classNames(
            'pill',
            mix,
            restProps.className,
            {
                pill_exclusion: variant === 'EXCLUSION',
                pill_addition: variant === 'ADDITION',
            },
            {
                pill_action: !!onClick,
                pill_pressed: pressed && onClick,
                pill__animation: animated,
                pill__remove: removed,
            },
        );

        const pillProps = {
            ...(interactive ? {tabIndex} : {}),
            ...restProps,
            as: (interactive ? 'button' : 'div') as 'button' | 'div',
            className: pillClassName,
            onMouseDown: handleMouseDown,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseLeave,
            onClick: handleClick,
            onAnimationEnd: handleAnimationEnd,
            onKeyUp: handleKeyUp,
        };

        const content = (
            <>
                {prefix ? <span className={classes.pill__prefix}>{prefix}</span> : null}
                {disableBrowserHint ? (
                    <span className={classNames('pill__name ellipsis')}>{name}</span>
                ) : (
                    <EllipsisTextHint mix={classes.pill__name}>{name}</EllipsisTextHint>
                )}
                {removable ? (
                    <span
                        ref={removePillRef}
                        className={classes['pill__action-button']}
                        aria-hidden="true">
                        <Icon
                            aria-label="Delete"
                            className={classes.pill__icon}
                            type={Icons.CLOSE}
                            onClick={!removableOnMouseDown ? handleRemove : noop}
                            onMouseEnter={handleMouseEnterRemove}
                            onMouseDown={removableOnMouseDown ? handleRemove : noop}
                        />
                    </span>
                ) : null}
            </>
        );

        if (hidden) {
            return null;
        }

        return (
            <PillBase
                ref={mergeRefs(actualRef as Ref<HTMLButtonElement>, arrowNavigationRef as Ref<HTMLButtonElement>)}
                {...pillProps}>
                {content}
            </PillBase>
        );
    },
);

Pill.displayName = 'Pill';
